import { useState, useEffect } from "react";
import { Bell, Check, X, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "@/lib/toast";

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  data: any;
  read: boolean;
  created_at: string;
}

export function NotificationsPopover() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const loadNotifications = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .eq('read', false)
        .in('type', ['team_invite', 'collaborator_invite'])
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading notifications:', error);
        return;
      }

      setNotifications(data || []);
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  };

  useEffect(() => {
    if (user) {
      loadNotifications();
    }
  }, [user]);

  const markAsRead = async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', notificationId);

      if (error) throw error;

      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleTeamInviteResponse = async (notification: Notification, accept: boolean) => {
    try {
      setIsLoading(true);
      
      if (accept) {
        const { error } = await supabase.rpc('accept_team_invite_from_notification', {
          invite_id: notification.data.invite_id
        });

        if (error) throw error;
        
        toast.success("Convite aceito com sucesso!");
      } else {
        // Decline invite
        const { error } = await supabase
          .from('team_invites')
          .update({ accepted: false })
          .eq('id', notification.data.invite_id);

        if (error) throw error;
        
        toast.success("Convite recusado");
      }
      
      await markAsRead(notification.id);
    } catch (error: any) {
      console.error('Error handling team invite:', error);
      toast.error(error.message || "Erro ao processar convite");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCollaboratorInviteResponse = async (notification: Notification, accept: boolean) => {
    try {
      setIsLoading(true);
      
      if (accept) {
        const { error } = await supabase.rpc('accept_collaborator_invite', {
          invite_token: notification.data.token
        });

        if (error) throw error;
        
        toast.success("Convite de colaboração aceito!");
      } else {
        // Decline collaborator invite
        const { error } = await supabase
          .from('collaborator_invites')
          .update({ status: 'declined' })
          .eq('token', notification.data.token);

        if (error) throw error;
        
        toast.success("Convite de colaboração recusado");
      }
      
      await markAsRead(notification.id);
    } catch (error: any) {
      console.error('Error handling collaborator invite:', error);
      toast.error(error.message || "Erro ao processar convite de colaboração");
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderNotificationAction = (notification: Notification) => {
    if (notification.type === 'team_invite') {
      return (
        <div className="flex gap-2 mt-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleTeamInviteResponse(notification, true)}
            disabled={isLoading}
          >
            <Check className="h-3 w-3 mr-1" />
            Aceitar
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleTeamInviteResponse(notification, false)}
            disabled={isLoading}
          >
            <X className="h-3 w-3 mr-1" />
            Recusar
          </Button>
        </div>
      );
    }

    if (notification.type === 'collaborator_invite') {
      return (
        <div className="flex gap-2 mt-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleCollaboratorInviteResponse(notification, true)}
            disabled={isLoading}
          >
            <Check className="h-3 w-3 mr-1" />
            Aceitar
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleCollaboratorInviteResponse(notification, false)}
            disabled={isLoading}
          >
            <X className="h-3 w-3 mr-1" />
            Recusar
          </Button>
        </div>
      );
    }

    return (
      <Button
        size="sm"
        variant="ghost"
        onClick={() => markAsRead(notification.id)}
        className="mt-2"
      >
        Marcar como lida
      </Button>
    );
  };

  if (notifications.length === 0) {
    return null;
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="relative">
          <Bell className="h-4 w-4" />
          {notifications.length > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs"
            >
              {notifications.length}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end">
        <div className="space-y-2">
          <h4 className="font-medium leading-none">Notificações</h4>
          <p className="text-sm text-muted-foreground">
            Você tem {notifications.length} convite(s) pendente(s)
          </p>
        </div>
        <Separator className="my-4" />
        <ScrollArea className="h-80">
          <div className="space-y-4">
            {notifications.map((notification) => (
              <div key={notification.id} className="space-y-2">
                <div className="flex items-start gap-3">
                  <UserPlus className="h-4 w-4 mt-1 text-blue-500" />
                  <div className="flex-1 space-y-1">
                    <p className="text-sm font-medium">{notification.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {notification.message}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatDate(notification.created_at)}
                    </p>
                    {renderNotificationAction(notification)}
                  </div>
                </div>
                {notifications.indexOf(notification) < notifications.length - 1 && (
                  <Separator />
                )}
              </div>
            ))}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}

// Keep the old export for backward compatibility
export function PendingInvitations() {
  return <NotificationsPopover />;
}
